import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Layout, Search, ChevronRight, Palette, Cpu,
  ArrowUpRight, CheckCircle2, Circle, Terminal, PlayCircle,
  Sparkles, Snowflake, Globe, LayoutDashboard, Bot, Megaphone, Workflow, BarChart3,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { formatApiError } from '../utils/apiError';
import DashboardShell from '../components/DashboardShell';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import useEntitlements from '../hooks/useEntitlements';
import { resolveAliFullStackBase } from '../lib/alifullstack';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '') ? process.env.REACT_APP_BACKEND_URL : '';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const { limits } = useEntitlements();
  const [websites, setWebsites] = useState([]);
  const [snagcartProjects, setSnagcartProjects] = useState([]);
  const [alifullstackApps, setAlifullstackApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNameModal, setShowNameModal] = useState(false);
  const [websiteNameInput, setWebsiteNameInput] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameChecking, setNameChecking] = useState(false);
  const [nameCreating, setNameCreating] = useState(false);

  const siteLimit = limits?.websites;
  const atWebsiteCap = Boolean(
    siteLimit && !siteLimit.unlimited && typeof siteLimit.remaining === 'number' && siteLimit.remaining <= 0,
  );

  useEffect(() => { fetchWebsites(); fetchSnagcartProjects(); fetchAliFullStackApps(); }, []);

  // If the user lands here from the Odoo templates page via its "Back to
  // dashboard" link, the URL carries ?from=odoo. Strip it and force a fresh
  // sync immediately + show a friendly toast so the new site is visible
  // without waiting for the focus listener.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('from') === 'odoo') {
      toast.loading('Syncing your latest template…', { id: 'odoo-back' });
      fetchWebsites().finally(() => toast.dismiss('odoo-back'));
      // Clean the query string so reloads don't re-trigger
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user comes back to this tab (e.g. after closing the Odoo
  // templates tab), refetch the website list so the new template-created
  // site shows up immediately.
  useEffect(() => {
    const refreshAll = () => { fetchWebsites(); fetchAliFullStackApps(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshAll();
    };
    window.addEventListener('focus', refreshAll);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshAll);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNameModal = () => {
    if (atWebsiteCap) {
      toast.error(
        `Your plan allows ${siteLimit.limit} website${siteLimit.limit === 1 ? '' : 's'}. Upgrade to create more.`,
      );
      navigate('/subscription-plans');
      return;
    }
    setWebsiteNameInput('');
    setNameError('');
    setShowNameModal(true);
  };

  const checkNameAvailability = async (name) => {
    if (!name.trim()) return;
    setNameChecking(true);
    setNameError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/websites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const sites = await res.json().catch(() => []);
        const taken = Array.isArray(sites)
          && sites.some((s) => (s.name || '').trim().toLowerCase() === name.trim().toLowerCase());
        if (taken) {
          setNameError(`"${name.trim()}" is already taken. Please choose a different name.`);
        }
      }
    } catch (e) {}
    setNameChecking(false);
  };

  const USE_ODOO_TEMPLATES = process.env.REACT_APP_USE_ODOO_TEMPLATES === 'true';

  const openNativeTemplateGallery = (name) => {
    const params = new URLSearchParams();
    if (name) params.set('name', name.trim());
    const qs = params.toString();
    navigate(`/generate${qs ? `?${qs}` : ''}`);
  };

  const ODOO_HOST = process.env.REACT_APP_ODOO_URL || 'https://odoo-production-4cc6.up.railway.app';
  const ODOO_TEMPLATES_URL = `${ODOO_HOST}/snagcart/templates`;

  const fetchOdooTemplatesUrl = async ({ websiteId, name } = {}) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (websiteId) params.set('website_id', String(websiteId));
    if (name) params.set('name', name);
    const qs = params.toString();
    const res = await fetch(
      `${API_URL}/api/odoo/templates-url${qs ? `?${qs}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Could not open template gallery');
    }
    return res.json();
  };

  const openTemplateGallery = async ({ websiteId, name, toastId } = {}) => {
    if (!USE_ODOO_TEMPLATES) {
      openNativeTemplateGallery(name);
      return true;
    }
    const loadingId = toastId || 'odoo-templates';
    try {
      toast.loading('Opening template gallery…', { id: loadingId });
      const data = await fetchOdooTemplatesUrl({ websiteId, name });
      toast.dismiss(loadingId);
      const targetRaw = data.auto_login_url || data.redirect_url || data.plain_url || ODOO_TEMPLATES_URL;
      const target = targetRaw.startsWith('/') ? `${window.location.origin}${targetRaw}` : targetRaw;
      window.open(target, '_blank', 'noopener,noreferrer');
      toast.success('Template gallery opened — pick from 95+ designs.', { duration: 5000 });
      return true;
    } catch (e) {
      toast.dismiss(loadingId);
      toast.error(e.message || 'Could not open template gallery. Please try again.');
      return false;
    }
  };

  const createNewOdooWebsite = async (websiteName) => {
    const name = (websiteName || websiteNameInput || '').trim();
    if (!name) { setNameError('Please enter a website name.'); return; }
    if (nameError) return;

    if (!USE_ODOO_TEMPLATES) {
      setShowNameModal(false);
      openNativeTemplateGallery(name);
      return;
    }

    setNameCreating(true);
    try {
      const token = localStorage.getItem('token');
      toast.loading('Opening template gallery…', { id: 'odoo-new' });
      const res = await fetch(`${API_URL}/api/odoo/new-website`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ website_name: name }),
      });
      toast.dismiss('odoo-new');
      if (res.status === 409) {
        const err = await res.json();
        setNameError(err.detail || 'This name is already taken.');
        setNameCreating(false);
        return;
      }
      if (!res.ok) {
        // Backend couldn't create the Odoo website — still try auto-login gallery
        // so the user lands on templates instead of the Odoo login page.
        const opened = await openTemplateGallery({ name, toastId: 'odoo-new' });
        if (opened) setShowNameModal(false);
        setNameCreating(false);
        return;
      }
      const data = await res.json();
      setShowNameModal(false);
      // Immediately refresh the websites list so the new site appears in
      // My Websites right away — no need to wait for the user to come back.
      fetchWebsites();
      // data.redirect_url is the bridge auto-login URL — drops the user
      // straight into the template gallery already authenticated.
      const targetRaw = data.redirect_url || data.configurator_url;
      const target = targetRaw && targetRaw.startsWith('/')
        ? `${window.location.origin}${targetRaw}`
        : targetRaw;
      if (target) {
        window.open(target, '_blank', 'noopener,noreferrer');
        toast.success('Template gallery opened — your new site is already in My Websites below.', { duration: 6000 });
      } else {
        await openTemplateGallery({ websiteId: data.website_id, name, toastId: 'odoo-new' });
      }
    } catch (e) {
      toast.dismiss('odoo-new');
      toast.error('Could not open template gallery. Please try again.');
    }
    setNameCreating(false);
  };

  const openOdooEditor = async (website) => {
    if (!website?.odoo_website_id) {
      navigate(`/edit/${website.id}`);
      return;
    }
    const ODOO_HOST = process.env.REACT_APP_ODOO_URL || 'https://odoo-production-4cc6.up.railway.app';
    const editPath = `/website/force/${website.odoo_website_id}?path=%2Fsnagcart-home%3Fenable_editor%3D1`;
    const fallbackUrl = `${ODOO_HOST}${editPath}`;

    // Strategy:
    // 1. Synchronously open /odoo-redirect (same-origin) so popup blockers
    //    don't interfere — the tab shows "Opening editor…" while we fetch.
    // 2. Fetch the auto-login URL from the backend (async, after open).
    // 3. Post the URL to the waiting tab via BroadcastChannel.
    // The OdooRedirect component listens on the same channel and immediately
    // does window.location.replace() once it receives the URL.
    const channelId = `odoo-editor-${Date.now()}`;
    const storageKey = `odoo_editor_url_${channelId}`;
    window.open(`/odoo-redirect?channel=${encodeURIComponent(channelId)}`, '_blank');

    // Hand the URL to the trampoline tab. BroadcastChannel is fast but does
    // NOT buffer — if we post before the new tab subscribes, the message is
    // lost and the tab times out. So we ALSO write the URL to localStorage,
    // which the trampoline polls; that makes the handoff race-proof.
    const bc = new BroadcastChannel(channelId);
    const deliver = (url) => {
      try { localStorage.setItem(storageKey, url); } catch (_) {}
      try { bc.postMessage({ url }); } catch (_) {}
    };
    // If the trampoline announces it's ready (it may mount after we post),
    // re-post so the URL is delivered even on the slow-tab ordering.
    bc.onmessage = (event) => {
      if ((event.data || {}).ready) {
        const u = (() => { try { return localStorage.getItem(storageKey); } catch (_) { return null; } })();
        if (u) { try { bc.postMessage({ url: u }); } catch (_) {} }
      }
    };
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/odoo/edit-url/${website.odoo_website_id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = res.ok ? await res.json() : null;
      let target = data?.auto_login_url || data?.redirect_url || fallbackUrl;
      if (target.startsWith('/')) target = `${window.location.origin}${target}`;
      deliver(target);
    } catch (_) {
      deliver(fallbackUrl);
    } finally {
      // Keep the channel open long enough for the new tab to mount + ack, then
      // clean up the localStorage key so it can't be reused stale.
      setTimeout(() => { bc.close(); try { localStorage.removeItem(storageKey); } catch (_) {} }, 20000);
    }
  };

  const fetchWebsites = async () => {
    try {
      const token = localStorage.getItem('token');
      // Sync Odoo websites into MongoDB first (best-effort, don't block on failure)
      try {
        await fetch(`${API_URL}/api/odoo/sync-websites`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (_) {}
      const response = await fetch(`${API_URL}/api/websites`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      setWebsites(data);
    } catch (error) {
      toast.error('Failed to load websites');
    } finally {
      setLoading(false);
    }
  };

  const deleteWebsite = async (websiteId) => {
    if (!window.confirm('Are you sure you want to delete this website?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/websites/${websiteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Website deleted successfully');
        fetchWebsites();
        return;
      }
      const payload = await response.json().catch(() => ({}));
      toast.error(formatApiError(payload, 'Failed to delete website'));
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    }
  };

  const fetchSnagcartProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await res.json();
      setSnagcartProjects(Array.isArray(data) ? data : []);
    } catch {
      // Snagcart Builder list is optional — silent failure
    }
  };

  // Snag Builder (AliFullStack) apps live in the gateway's per-tenant DB, not the
  // emerge websites collection. Pull them via the same-origin /alifullstack-api
  // IPC proxy using the emerge token so the tenant matches the embedded builder.
  const fetchAliFullStackApps = async () => {
    try {
      const token = localStorage.getItem('token');
      const base = resolveAliFullStackBase();
      const res = await fetch(`${base}/ipc/invoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ channel: 'list-apps', args: [] }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const apps = data?.result?.apps;
      setAlifullstackApps(Array.isArray(apps) ? apps : []);
    } catch {
      // Snag Builder app list is optional — silent failure if gateway is down
    }
  };

  const deleteAliFullStackApp = async (appId, appName) => {
    if (!window.confirm(`Delete the Snag Builder app "${appName || appId}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      const base = resolveAliFullStackBase();
      const res = await fetch(`${base}/ipc/invoke`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ channel: 'delete-app', args: [{ appId }] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.error) throw new Error(data?.error || `HTTP ${res.status}`);
      toast.success('Snag Builder app deleted');
      fetchAliFullStackApps();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  const deleteSnagcartProject = async (jobId) => {
    if (!window.confirm('Delete this Snagcart Builder project and its workspace? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/gpt-pilot/projects/${jobId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Snagcart Builder project deleted');
      fetchSnagcartProjects();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  const openDevonz = () => {
    // Devonz is mounted under /devonz/ on the same host (proxied to port 4000).
    const devonzUrl = window.location.origin + '/devonz/';
    window.open(devonzUrl, '_blank', 'noopener,noreferrer');
  };

  const openAppify = () => {
    // Open-Lovable runs on its own Next.js dev server (CRA proxy serves Emergent index.html).
    const hostname = window.location.hostname || 'localhost';
    const appifyUrl = `http://${hostname}:3012/appify/`;
    window.open(appifyUrl, '_blank', 'noopener,noreferrer');
  };

  const openGptEngineer = () => {
    const hostname = window.location.hostname || 'localhost';
    window.open(`http://${hostname}:3015/`, '_blank', 'noopener,noreferrer');
  };

  const openPingcapBuilder = async () => {
    const apiBase = API_URL || '';
    const hostname = window.location.hostname || 'localhost';
    const directUrl = `http://${hostname}:3017/`;
    try {
      toast.loading('Starting TiDB Builder…', { id: 'pingcap-start' });
      const statusRes = await fetch(`${apiBase}/api/services/pingcap-builder/status`);
      if (!statusRes.ok) throw new Error(`status HTTP ${statusRes.status}`);
      const status = await statusRes.json();
      if (!status.running) {
        const startRes = await fetch(`${apiBase}/api/services/pingcap-builder/start`, { method: 'POST' });
        if (!startRes.ok) {
          const detail = await startRes.json().catch(() => ({}));
          throw new Error(detail.detail || `start HTTP ${startRes.status}`);
        }
        const started = await startRes.json();
        if (started.status === 'starting') {
          toast.info(started.message || 'TiDB Builder is still starting — opening UI…', { id: 'pingcap-start' });
        } else {
          toast.success('TiDB Builder is ready', { id: 'pingcap-start' });
        }
      } else {
        toast.dismiss('pingcap-start');
      }
    } catch (e) {
      toast.error(`TiDB Builder: ${e.message}`, { id: 'pingcap-start' });
    }
    window.open(directUrl, '_blank', 'noopener,noreferrer');
  };

  const openDecember = async () => {
    const apiBase = API_URL || '';
    const proxyUrl = `${window.location.origin}/december`;
    try {
      toast.loading('Starting December…', { id: 'december-start' });
      const statusRes = await fetch(`${apiBase}/api/services/december/status`);
      if (!statusRes.ok) throw new Error(`status HTTP ${statusRes.status}`);
      const status = await statusRes.json();
      if (!status.running) {
        const startRes = await fetch(`${apiBase}/api/services/december/start`, { method: 'POST' });
        if (!startRes.ok) {
          const detail = await startRes.json().catch(() => ({}));
          throw new Error(detail.detail || `start HTTP ${startRes.status}`);
        }
        const started = await startRes.json();
        if (started.status === 'starting') {
          toast.info(started.message || 'December is still starting — opening UI…', { id: 'december-start' });
        } else {
          toast.success('December is ready', { id: 'december-start' });
        }
      } else {
        toast.dismiss('december-start');
      }
    } catch (e) {
      toast.error(`December: ${e.message}`, { id: 'december-start' });
    }
    window.open(proxyUrl, '_blank', 'noopener,noreferrer');
  };

  const quickActions = [
    { title: 'Create from Templates', description: 'Start with a professionally designed template', icon: Layout, gradient: 'from-emerald-600 to-teal-600', action: () => openTemplateGallery() },
    { title: 'Snagcart Builder', description: 'Step-by-step planner → spec → architect → code. Best for full-stack apps.', icon: Cpu, gradient: 'from-emerald-600 to-teal-600', action: () => navigate('/gpt-pilot') },
  ];

  const filteredWebsites = websites.filter(w =>
    w.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSnagcartProjects = snagcartProjects.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAliFullStackApps = alifullstackApps.filter(a =>
    (a.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalProjectCount = filteredWebsites.length + filteredSnagcartProjects.length + filteredAliFullStackApps.length;

  return (
    <>
      <DashboardShell
        user={user}
        title="My Websites"
        subtitle="Manage and monitor your AI-generated websites"
        activeNav="websites"
        headerExtras={(
          <div className="relative hidden sm:block">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search websites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white/[0.05] border border-white/[0.08] rounded-lg pl-9 pr-4 py-1.5 text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-[#fbbf0f]/50 focus:bg-white/[0.07] transition-all w-48"
              data-testid="dashboard-search-input"
            />
          </div>
        )}
      >
        <div className="px-6 py-6 max-w-6xl" data-testid="dashboard-page">
          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">Quick Actions</h2>
            <ModuleDemoVideo moduleKey="create-from-template" className="mb-4" />
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <button
                onClick={() => navigate('/snagbuilder')}
                data-testid="quick-action-alifullstack"
                className="group relative p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#fbbf0f]/40 hover:bg-[#fbbf0f]/[0.06] transition-all text-left overflow-hidden"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#fbbf0f] to-[#ef9f00] border-2 border-[#16130d] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Cpu className="w-4 h-4 text-[#16130d]" />
                </div>
                <p className="text-sm font-semibold text-white mb-0.5">Snag Builder</p>
                <p className="text-[11px] text-zinc-500 leading-relaxed">AI full-stack builder — instant access for your team (shared server instance).</p>
                <ArrowUpRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-[#fbbf0f] absolute top-3.5 right-3.5 transition-colors" />
              </button>
              <button
                onClick={() => openTemplateGallery()}
                data-testid="quick-action-create-from-template"
                className="group relative p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.05] transition-all text-left overflow-hidden"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                  <Layout className="w-4 h-4 text-white" />
                </div>
                <p className="text-sm font-semibold text-white mb-0.5">Create from Template</p>
                <p className="text-[11px] text-zinc-500 leading-relaxed">Start from 95+ professionally designed templates — customise and publish in minutes.</p>
                <ArrowUpRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400 absolute top-3.5 right-3.5 transition-colors" />
              </button>
            </div>
          </div>

          {/* Websites Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                My Websites
                {!loading && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-md bg-white/[0.06] text-zinc-400 text-[10px] font-normal normal-case">
                    {totalProjectCount}
                    {siteLimit?.unlimited
                      ? ' · unlimited'
                      : siteLimit?.limit != null
                        ? ` / ${siteLimit.limit}`
                        : ''}
                  </span>
                )}
              </h2>
              <button
                onClick={openNameModal}
                data-testid="create-website-button"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold transition-all ${
                  atWebsiteCap
                    ? 'bg-zinc-700 hover:bg-zinc-600'
                    : 'bg-orange-500 hover:bg-orange-400'
                }`}
              >
                <Plus className="w-3.5 h-3.5" />
                {atWebsiteCap ? 'Upgrade for more sites' : 'Create Website'}
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20" data-testid="loading-state">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-[#fbbf0f] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-xs text-zinc-500">Loading your websites...</p>
                </div>
              </div>
            ) : totalProjectCount === 0 ? (
              <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/[0.06] border-dashed" data-testid="empty-state">
                <div className="w-12 h-12 rounded-2xl bg-[#fbbf0f]/15 border-2 border-[#16130d] flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-[#fbbf0f]" />
                </div>
                <h3 className="text-base font-semibold text-white mb-1">
                  {searchQuery ? 'No websites found' : 'No websites yet'}
                </h3>
                <p className="text-sm text-zinc-500 mb-5">
                  {searchQuery ? `No results for "${searchQuery}"` : 'Create your first AI-powered website to get started'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={openNameModal}
                    data-testid="empty-create-button"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-sm font-semibold transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    Create Website
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredWebsites.map((website) => (
                  <div
                    key={website.id}
                    data-testid={`website-card-${website.id}`}
                    className="group relative p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.05] transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0 pr-3">
                        <h3 className="text-sm font-semibold text-white truncate mb-0.5" data-testid={`website-name-${website.id}`}>
                          {website.name}
                        </h3>
                        <p className="text-xs text-zinc-500 truncate" data-testid={`website-desc-${website.id}`}>
                          {website.source === 'app-builder'
                            ? `Snagcart App Builder${website.sitebrain_build_state ? ` · ${website.sitebrain_build_state}` : ''}`
                            : (website.description || 'No description')}
                        </p>
                      </div>
                      <span
                        className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          website.is_published || website.sitebrain_build_state === 'live'
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : website.source === 'app-builder' && website.sitebrain_build_state && !['live', 'idle'].includes(website.sitebrain_build_state)
                              ? 'bg-amber-500/10 text-amber-300'
                              : 'bg-zinc-800 text-zinc-500'
                        }`}
                        data-testid={`website-status-${website.id}`}
                      >
                        {website.is_published || website.sitebrain_build_state === 'live' ? (
                          <><CheckCircle2 className="w-2.5 h-2.5" />Live</>
                        ) : website.source === 'app-builder' && website.sitebrain_build_state && !['idle'].includes(website.sitebrain_build_state) ? (
                          <><Circle className="w-2.5 h-2.5" />{website.sitebrain_build_state}</>
                        ) : (
                          <><Circle className="w-2.5 h-2.5" />Draft</>
                        )}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 mb-4 text-[11px] text-zinc-600">
                      <span className="flex items-center gap-1" data-testid={`website-languages-${website.id}`}>
                        <Globe className="w-3 h-3" />
                        {website.languages?.join(', ') || 'en'}
                      </span>
                      <span>·</span>
                      <span data-testid={`website-currencies-${website.id}`}>
                        {website.currencies?.join(', ') || 'USD'}
                      </span>
                      {website.custom_domain && (
                        <>
                          <span>·</span>
                          <button
                            type="button"
                            onClick={() => navigate('/domains-hosting')}
                            className="truncate max-w-[140px] text-emerald-500/80 hover:text-emerald-400"
                            data-testid={`website-domain-${website.id}`}
                          >
                            {website.custom_domain}
                          </button>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          if (website.source === 'app-builder' && website.sitebrain_project_id) {
                            navigate(`/app-builder?project=${website.sitebrain_project_id}`);
                          } else if (website.source === 'odoo' && website.odoo_website_id) {
                            openOdooEditor(website);
                          } else {
                            navigate(`/edit/${website.id}`);
                          }
                        }}
                        data-testid={`edit-button-${website.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-[#fbbf0f] hover:bg-[#ffd84a] text-[#16130d] border-2 border-[#16130d] text-xs font-extrabold transition-all"
                      >
                        <Palette className="w-3.5 h-3.5" />
                        {website.source === 'app-builder' ? 'Open Builder' : 'Edit'}
                      </button>
                      {website.source !== 'app-builder' && website.source !== 'odoo' && (
                        <button
                          onClick={() => navigate(`/sites/${website.id}/operate`)}
                          data-testid={`operator-button-${website.id}`}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 hover:text-rose-200 border border-rose-500/20 transition-all"
                          title="Operator dashboard"
                        >
                          <LayoutDashboard className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/admin/${website.id}`)}
                        data-testid={`admin-button-${website.id}`}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-zinc-500 hover:text-zinc-300 border border-white/[0.06] transition-all"
                        title="Analytics"
                      >
                        <BarChart3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteWebsite(website.id)}
                        data-testid={`delete-button-${website.id}`}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-red-500/10 text-zinc-600 hover:text-red-400 border border-white/[0.06] hover:border-red-500/20 transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Snagcart Builder projects — each shown as its own segregated card */}
                {filteredSnagcartProjects.map((proj) => {
                  const shortId = proj.job_id.slice(0, 8);
                  const statusCls =
                    proj.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
                    proj.status === 'running' || proj.status === 'building' || proj.status === 'queued' ? 'bg-sky-500/10 text-sky-300' :
                    proj.status === 'failed' || proj.status === 'error' ? 'bg-red-500/10 text-red-400' :
                    'bg-zinc-800 text-zinc-500';
                  return (
                    <div
                      key={proj.job_id}
                      data-testid={`snagcart-card-${shortId}`}
                      className="group relative p-5 rounded-xl bg-white/[0.03] border border-[#fbbf0f]/30 hover:border-[#fbbf0f]/55 hover:bg-white/[0.05] transition-all"
                    >
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-[#fbbf0f] text-[#16130d] border border-[#16130d]">
                          <Cpu className="w-2.5 h-2.5" />
                          Snagcart Builder
                        </span>
                      </div>
                      <div className="flex items-start justify-between mb-4 pr-2">
                        <div className="flex-1 min-w-0 pr-3 mt-5">
                          <h3 className="text-sm font-semibold text-white truncate mb-0.5" data-testid={`snagcart-name-${shortId}`}>
                            {proj.name || '(unnamed)'}
                          </h3>
                          <p className="text-xs text-zinc-500 line-clamp-2" data-testid={`snagcart-desc-${shortId}`}>
                            {proj.description || 'No description'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-4 text-[11px] text-zinc-600">
                        <span className="font-mono text-zinc-500">{shortId}</span>
                        <span>·</span>
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${statusCls}`}
                          data-testid={`snagcart-status-${shortId}`}
                        >
                          {proj.status || 'unknown'}
                        </span>
                        {proj.last_deploy?.url && (
                          <a
                            href={proj.last_deploy.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 hover:text-emerald-200 border border-emerald-500/30 transition-colors"
                            data-testid={`snagcart-live-${shortId}`}
                            title={`Live on ${proj.last_deploy.provider || 'cloud'} — click to open ${proj.last_deploy.url}`}
                          >
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-300" />
                            </span>
                            Live
                          </a>
                        )}
                        {proj.created_at && (
                          <>
                            <span>·</span>
                            <span>{new Date(proj.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {proj.status === 'interrupted' && (
                          <button
                            onClick={() => navigate(`/gpt-pilot?job=${proj.job_id}&tab=chat&resume=1`)}
                            data-testid={`snagcart-resume-${shortId}`}
                            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 hover:border-amber-500/50 text-xs font-semibold transition-all"
                            title="Pick up where this interrupted build left off — opens chat to continue"
                          >
                            <PlayCircle className="w-3.5 h-3.5" />
                            Resume
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/gpt-pilot?job=${proj.job_id}`)}
                          data-testid={`snagcart-edit-${shortId}`}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-[#fbbf0f] hover:bg-[#ffd84a] text-[#16130d] border-2 border-[#16130d] text-xs font-extrabold transition-all"
                          title="Open this project in Snagcart Builder"
                        >
                          <Palette className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        <button
                          onClick={() => navigate(`/gpt-pilot?job=${proj.job_id}&tab=chat`)}
                          data-testid={`snagcart-agent-${shortId}`}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/20 hover:border-emerald-500/40 text-xs font-semibold transition-all"
                          title="Open Autonomous Vision Agent for this project"
                        >
                          <Bot className="w-3.5 h-3.5" />
                          AI Agent
                        </button>
                        <a
                          href={`${API_URL}/api/gpt-pilot/preview/${proj.job_id}/`}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid={`snagcart-preview-${shortId}`}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-zinc-500 hover:text-zinc-300 border border-white/[0.06] transition-all"
                          title="Open live preview in a new tab"
                        >
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                        <button
                          onClick={() => deleteSnagcartProject(proj.job_id)}
                          data-testid={`snagcart-delete-${shortId}`}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-red-500/10 text-zinc-600 hover:text-red-400 border border-white/[0.06] hover:border-red-500/20 transition-all"
                          title="Delete this project + its workspace"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Snag Builder (AliFullStack) apps — each shown as its own card */}
                {filteredAliFullStackApps.map((app) => {
                  const shortId = String(app.id);
                  return (
                    <div
                      key={`afs-${app.id}`}
                      data-testid={`alifullstack-card-${shortId}`}
                      className="group relative p-5 rounded-xl bg-white/[0.03] border border-emerald-500/[0.18] hover:border-emerald-500/[0.40] hover:bg-white/[0.05] transition-all"
                    >
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                          <Sparkles className="w-2.5 h-2.5" />
                          Snag Builder
                        </span>
                      </div>
                      <div className="flex items-start justify-between mb-4 pr-2">
                        <div className="flex-1 min-w-0 pr-3 mt-5">
                          <h3 className="text-sm font-semibold text-white truncate mb-0.5" data-testid={`alifullstack-name-${shortId}`}>
                            {app.name || '(unnamed)'}
                          </h3>
                          <p className="text-xs text-zinc-500 line-clamp-2">
                            {app.path || 'Snag Builder app'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-4 text-[11px] text-zinc-600">
                        <span className="font-mono text-zinc-500">#{shortId}</span>
                        {app.createdAt && (
                          <>
                            <span>·</span>
                            <span>{new Date(app.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/snagbuilder?appId=${app.id}`)}
                          data-testid={`alifullstack-open-${shortId}`}
                          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-all"
                          title="Open this app in Snag Builder"
                        >
                          <Palette className="w-3.5 h-3.5" />
                          Open in Snag Builder
                        </button>
                        <button
                          onClick={() => deleteAliFullStackApp(app.id, app.name)}
                          data-testid={`alifullstack-delete-${shortId}`}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-red-500/10 text-zinc-600 hover:text-red-400 border border-white/[0.06] hover:border-red-500/20 transition-all"
                          title="Delete this Snag Builder app"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Feature Highlights - always shown below the website list */}
          {!loading && !searchQuery && (
            <div className="mt-8">
              <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">Explore Features</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { icon: Sparkles, title: 'AI Co-founder', desc: 'Autonomous agent that runs your company 24/7', path: '/ai-cofounder', color: 'text-[#fbbf0f]', bg: 'bg-[#fbbf0f]/15' },
                  { icon: Cpu, title: 'AI Studio', desc: 'Build custom AI models and agents', path: '/ai-studio', color: 'text-[#fbbf0f]', bg: 'bg-[#fbbf0f]/15' },
                  { icon: Megaphone, title: 'Marketing Suite', desc: 'Email, SMS & social campaigns', path: '/marketing-suite', color: 'text-pink-400', bg: 'bg-pink-500/10' },
                  { icon: Workflow, title: 'Automations', desc: 'Automate workflows & tasks', path: '/automations', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                ].map((feat, i) => {
                  const Icon = feat.icon;
                  return (
                    <button
                      key={i}
                      onClick={() => navigate(feat.path)}
                      className="group flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.1] hover:bg-white/[0.04] transition-all text-left"
                    >
                      <div className={`w-9 h-9 rounded-xl ${feat.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${feat.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">{feat.title}</p>
                        <p className="text-xs text-zinc-500 truncate">{feat.desc}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DashboardShell>

      {/* Website Name Modal */}
      {showNameModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowNameModal(false); }}
        >
          <div className="w-full max-w-md bg-[#111111] border border-white/[0.1] rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                <Layout className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Name Your Website</h2>
                <p className="text-xs text-zinc-500">Choose a unique name for your new website</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-zinc-400 mb-2">Website Name</label>
              <input
                type="text"
                autoFocus
                value={websiteNameInput}
                onChange={(e) => {
                  setWebsiteNameInput(e.target.value);
                  setNameError('');
                }}
                onBlur={(e) => checkNameAvailability(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createNewOdooWebsite(); }}
                placeholder="e.g. My Salon, Tech Store, Portfolio..."
                className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500/60 focus:bg-white/[0.07] transition-all"
                maxLength={80}
              />
              {nameChecking && (
                <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1">
                  <span className="inline-block w-3 h-3 border border-zinc-500 border-t-transparent rounded-full animate-spin" />
                  Checking availability...
                </p>
              )}
              {nameError && (
                <p className="text-xs text-red-400 mt-1.5">{nameError}</p>
              )}
              {!nameError && !nameChecking && websiteNameInput.trim() && (
                <p className="text-xs text-emerald-400 mt-1.5">✓ Name is available</p>
              )}
            </div>

            <p className="text-[11px] text-zinc-600 mb-5">
              This name must be unique across all your websites. You can change it later in the website settings.
            </p>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowNameModal(false)}
                className="flex-1 py-2.5 rounded-lg border border-white/[0.1] text-zinc-400 text-sm font-semibold hover:bg-white/[0.04] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => createNewOdooWebsite()}
                disabled={nameCreating || nameChecking || !!nameError || !websiteNameInput.trim()}
                className="flex-1 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all flex items-center justify-center gap-2"
              >
                {nameCreating ? (
                  <><span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating...</>
                ) : (
                  <><Layout className="w-4 h-4" /> Start with Templates</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
